import type { FormEvent } from "react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { useForm, ValidationError } from "@formspree/react";

import { DESIGN_PARTNER_FORMSPREE_ID, DESIGN_PARTNER_PATHS } from "./routes";

type ApplyFields = {
  name: string;
  email: string;
  role: string;
  role_description: string;
  projects: string;
  pain: string;
  notes: string;
  consent: string;
};

const SUCCESS_COPY =
  "Thanks. If your workflow is a fit, we'll contact you to schedule a 30-minute discovery conversation. No trial account or payment is required.";

export function DesignPartnerApplyForm() {
  const [state, handleSubmit] = useForm<ApplyFields>(DESIGN_PARTNER_FORMSPREE_ID);
  const [consentChecked, setConsentChecked] = useState(false);
  const [clientError, setClientError] = useState<string | null>(null);

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    const form = event.currentTarget;
    setClientError(null);

    if (!form.checkValidity()) {
      event.preventDefault();
      form.reportValidity();
      setClientError("Please complete all required fields before submitting.");
      return;
    }

    if (!consentChecked) {
      event.preventDefault();
      setClientError("Please confirm the privacy consent checkbox before submitting.");
      return;
    }

    void handleSubmit(event);
  };

  if (state.succeeded) {
    return (
      <div className="success" role="status" data-testid="dp-apply-success">
        {SUCCESS_COPY}
      </div>
    );
  }

  return (
    <form className="form" onSubmit={onSubmit} noValidate data-testid="dp-apply-form">
      <input required name="name" aria-label="Your name" placeholder="Your name" autoComplete="name" />
      <input
        required
        type="email"
        name="email"
        aria-label="Work email"
        placeholder="Work email"
        autoComplete="email"
      />
      <ValidationError prefix="Email" field="email" errors={state.errors} />

      <select required name="role" aria-label="Your role" defaultValue="">
        <option value="" disabled>
          Your role
        </option>
        <option>Founder / Executive</option>
        <option>Team or department lead</option>
        <option>Project or program lead</option>
        <option>Operations leader</option>
        <option>Functional specialist leading an initiative</option>
        <option>Individual contributor coordinating work</option>
        <option>Other / Prefer to describe it myself</option>
      </select>

      <select required name="projects" aria-label="Active project count" defaultValue="">
        <option value="" disabled>
          Active projects
        </option>
        <option>1–5</option>
        <option>6–20</option>
        <option>21–50</option>
        <option>51+</option>
      </select>

      <input
        className="full"
        name="role_description"
        aria-label="Job title or role description (optional)"
        placeholder="Job title or role description (optional)"
        autoComplete="organization-title"
      />

      <select required className="full" name="pain" aria-label="Primary challenge" defaultValue="">
        <option value="" disabled>
          Primary challenge
        </option>
        <option>Project visibility and status reporting</option>
        <option>Approvals and governance</option>
        <option>Risk and dependencies</option>
        <option>Capacity and resource conflicts</option>
        <option>Other</option>
      </select>

      <textarea
        className="full"
        name="notes"
        aria-label="Project management challenge"
        placeholder="Briefly describe the project-management challenge you are trying to solve (optional)"
      />

      <label className="consent">
        <input
          required
          type="checkbox"
          name="consent"
          value="yes"
          checked={consentChecked}
          onChange={(e) => setConsentChecked(e.target.checked)}
        />{" "}
        I agree that Zephix may use my information to evaluate design-partner fit and contact me about
        a discovery conversation. See the <Link to={DESIGN_PARTNER_PATHS.privacy}>Privacy Policy</Link>.
      </label>

      {clientError ? (
        <p className="full" role="alert" data-testid="dp-apply-client-error" style={{ color: "#f5c2c2" }}>
          {clientError}
        </p>
      ) : null}

      <ValidationError prefix="Form" errors={state.errors} />

      <button className="btn primary" type="submit" disabled={state.submitting} data-testid="dp-apply-submit">
        {state.submitting ? "Sending…" : "Request a design-partner conversation →"}
      </button>
    </form>
  );
}
